import { useTheme } from '@emotion/react';
import { Picture } from '../picture/picture';
import ViewsSVG from '../../public/img/views2.svg';
import { Link } from '../styled/Link';
import PersonSVG from '../../public/img/person-red.svg';
import FavoriteButton from '../favorite/FavoriteButton';
import FavoriteButtonCardWrapper from '../favorite/FavoriteButtonCardWrapper';
import { FavoriteKinds } from '../../models/favorite';
import { PersonInListV3 } from '../../models/person';

type IPersonCardProps = {
  item: PersonInListV3;
};

export function PersonCard({ item }: IPersonCardProps) {
  const theme = useTheme();

  const generateSpecializationTitles = (person: PersonInListV3) => {
    if (person.specialization_titles) {
      return person.specialization_titles.map((prof) => (
        <div
          key={`PersonSVGWrapper__${prof}`}
          css={{ display: 'flex', alignItems: 'center' }}
        >
          <PersonSVG
            css={{
              width: 16,
              height: 16,
              stroke: theme.colors.red,
              fill: theme.colors.white,
              marginRight: 8,
            }}
          />
          {prof}
        </div>
      ));
    }
    // favorites v5
    if (person.specializations) {
      return person.specializations.map((prof) => (
        <div
          key={`PersonSVGWrapper__${prof.slug}`}
          css={{ display: 'flex', alignItems: 'center' }}
        >
          <PersonSVG
            css={{
              width: 16,
              height: 16,
              stroke: theme.colors.red,
              fill: theme.colors.white,
              marginRight: 8,
            }}
          />
          {prof.title}
        </div>
      ));
    }
    return null;
  };

  return (
    <div
      // actions__item actions__item_scroll
      itemScope
      itemType='https://schema.org/Person'
      css={{
        paddingRight: 8,
        paddingLeft: 8,
        marginBottom: '16px',
      }}
    >
      <div
        css={{
          display: 'flex',
          height: '100%',
          width: '100%',
        }}
      >
        <div
          // action-card
          css={{
            height: '100%',
            borderRadius: '4px',
            backgroundColor: '#fff',
            position: 'relative',
            top: 0,
            width: '100%',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: theme.constants.boxShadow,
            },
          }}
        >
          <Link
            // person-card__image-wrapper
            itemProp='url'
            href={['', 'person', item.slug, ''].join('/')}
            css={{
              height: 0,
              width: '100%',
              paddingBottom: '75%',
              position: 'relative',
              display: 'block',
              '&:hover': {
                color: theme.colors.black,
              },
            }}
          >
            {item.first_image && (
              <div
                // action-card__image
                css={{
                  borderRadius: '4px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  overflow: 'hidden',
                }}
              >
                <Picture
                  src={item.first_image}
                  is360
                  alt={`${item.first_name} ${item.last_name}`}
                />
              </div>
            )}
          </Link>
          <div
            // person-card__info
            css={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '20px 20px 40px',
            }}
          >
            <div
              // person-card__specialization
              css={{
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                flexWrap: 'wrap',
                color: theme.colors.text,
                fontFamily: 'Golos',
                fontWeight: 400,
                fontSize: '12px',
                marginBottom: 2,
              }}
            >
              {generateSpecializationTitles(item)}
            </div>
            <Link
              // person-card__title
              href={['', 'person', item.slug, ''].join('/')}
              css={{
                fontFamily: 'Montserrat',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '22px',
                letterSpacing: '0em',
                color: theme.colors.text,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                '&:hover': {
                  color: theme.colors.text,
                },
              }}
            >
              <span itemProp='name'>
                {item.first_name} {item.last_name}
              </span>
            </Link>
            <FavoriteButtonCardWrapper>
              <FavoriteButton
                kind={FavoriteKinds.person}
                id={item.id}
                size='small'
                variant='red-outline'
              />
            </FavoriteButtonCardWrapper>
            <div
              css={{
                position: 'absolute',
                bottom: '12px',
                right: '20px',
                left: '20px',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                color: theme.colors.grayStrong,
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '18px',
                  height: '16px',
                  fontFamily: 'Golos',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '16px',
                  letterSpacing: '0.01em',
                  svg: {
                    flex: '0 0 auto',
                    marginRight: '5px',
                  },
                }}
              >
                <ViewsSVG
                  fill={theme.colors.grayStrong}
                  css={{ width: '16px', height: '16px' }}
                />
                <span>{item.views}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
